import { Copy, Edit, Package, Plus, Trash2, Wrench } from 'lucide-react';
import { useState } from 'react';

import { EmptyState } from '@/components/common/EmptyState';
import { Screen } from '@/components/layout/Screen';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from '@/components/ui/toast';
import { useAuth } from '@/hooks/useAuth';
import {
  useDeleteOffering,
  useDuplicateOffering,
  useOfferings,
  type OfferingRecord,
} from '@/hooks/useLineData';

import { OfferingFormDialog } from './OfferingFormDialog';

function OfferingList({ tag }: { tag: 'service' | 'product' }) {
  const { user } = useAuth();
  const canEdit = user?.role === 'owner' || user?.role === 'editor';
  const { data, isLoading } = useOfferings(tag);
  const deleteMutation = useDeleteOffering();
  const duplicateMutation = useDuplicateOffering();
  const [editing, setEditing] = useState<OfferingRecord | null>(null);
  const [creating, setCreating] = useState(false);

  const handleDelete = async (o: OfferingRecord) => {
    if (!window.confirm(`「${o.name}」を削除します。よろしいですか？`)) return;
    try {
      await deleteMutation.mutateAsync(o.id);
      toast.success('削除しました');
    } catch (err) {
      toast.error(`削除に失敗しました: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const handleDuplicate = async (o: OfferingRecord) => {
    try {
      await duplicateMutation.mutateAsync(o.id);
      toast.success('複製しました（PDF は再アップロードしてください）');
    } catch (err) {
      toast.error(`複製に失敗しました: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const Icon = tag === 'service' ? Wrench : Package;

  return (
    <div>
      {canEdit && (
        <div className="flex justify-end mb-4">
          <Button type="button" variant="primary" onClick={() => setCreating(true)}>
            <Plus />
            {tag === 'service' ? 'サービス追加' : '製品追加'}
          </Button>
        </div>
      )}

      {isLoading ? (
        <Spinner />
      ) : data && data.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {data.map((o) => (
            <div
              key={o.id}
              className="rounded bg-[var(--bg-surface)] border border-[var(--border)] p-4 hover:border-[var(--text-muted)] transition-colors"
            >
              <div className="flex items-start gap-3 mb-2">
                <Icon className="size-5 text-[var(--accent)] flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-sm truncate">{o.name}</h3>
                    <Badge variant="accent">{o.tag}</Badge>
                  </div>
                  <p className="text-xs text-[var(--text-secondary)] line-clamp-2 leading-relaxed">
                    {o.descriptionText || '(説明未設定)'}
                  </p>
                </div>
              </div>
              <div className="text-xs text-[var(--text-muted)] flex items-center gap-3 mt-2">
                <span>PDF: {o.pdfFiles?.length ?? 0} 件</span>
                <span>FAQ: {o.faq?.length ?? 0} 件</span>
                {o.websiteUrl && <span className="truncate">Web あり</span>}
              </div>
              {canEdit && (
                <div className="flex justify-end gap-1 mt-3 pt-3 border-t border-[var(--border)]">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditing(o)}
                  >
                    <Edit />
                    編集
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDuplicate(o)}
                    disabled={duplicateMutation.isPending}
                    title="複製"
                  >
                    <Copy />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(o)}
                  >
                    <Trash2 />
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <EmptyState
          icon={Icon}
          title={`${tag === 'service' ? 'サービス' : '製品'}が登録されていません`}
          description="LINE Bot が query_service_info / query_product_info で参照するデータです"
        />
      )}

      <OfferingFormDialog
        open={creating || !!editing}
        tag={tag}
        existing={editing}
        onOpenChange={(o) => {
          if (!o) {
            setCreating(false);
            setEditing(null);
          }
        }}
      />
    </div>
  );
}

export function OfferingsPage() {
  return (
    <Screen
      title="商材管理"
      description="LINE Bot が参照するサービス・製品情報を管理します"
    >
      <Tabs defaultValue="service">
        <TabsList>
          <TabsTrigger value="service">サービス</TabsTrigger>
          <TabsTrigger value="product">製品</TabsTrigger>
        </TabsList>
        <TabsContent value="service">
          <OfferingList tag="service" />
        </TabsContent>
        <TabsContent value="product">
          <OfferingList tag="product" />
        </TabsContent>
      </Tabs>
    </Screen>
  );
}
